import { IMakeModelTierActualResponse } from "api/interfaces/get-page-details/tier-make-model.type";
import { ITierActualResponse } from "api/interfaces/get-page-details/tier-page.type";
import { DELIVERY_METHODS } from "constant/action-constants";
import axios from "fetch-api/fetch-api";
import { validQueryParams } from "modules/category/constants/constants";
import qs from "query-string";
import { getStore } from "store";
import { DESKTOP_ITEM_COUNT, MOBILE_ITEM_COUNT } from "util/category/category-utils";
import { CATEGORY_PAGE_TYPES } from "util/product/product-constants";
import { omit } from "./lodash/omit";
import { pick } from "./lodash/pick";
import PASentry from "./pa-sentry";
import { getPageDetails } from "./url/apiConstants";
import filterUrl from "./url/filterUrl";
import { getFragmentParams } from "./url/getFragmentParams";
import { API_PARAM_ORDERS } from "./url/urlHelper";
import { validateVehicle } from "./vehicle/validate-vehicle";

const apiCallsInTransit = new Set<string>();
let isMounted = false;

interface IParams {
  pathname: string | string[] | undefined;
  isMobile: boolean;
  selectedVehicle: any;
  skipVehicleSelectorModal?: boolean;
  serverRequestUrl: string;
  actualResponse?: (ITierActualResponse & IMakeModelTierActualResponse) | Record<string, never>;
}

export function refreshData({
  pathname,
  isMobile,
  selectedVehicle,
  skipVehicleSelectorModal = false,
  serverRequestUrl = "",
  actualResponse = {},
}: IParams) {
  return new Promise((resolve) => {
    const store = getStore();
    const { urlInformation, currentPageType } = store.getState().utils;
    const { businessCustomer } = store.getState().authenticationState;
    const parsed = qs.parse(location.search);
    const { year: fragmentYear, make: fragmentMake, model: fragmentModel } = getFragmentParams();
    const queryParams = pick(parsed, validQueryParams);

    queryParams.url = pathname;
    queryParams.numberOfProducts = isMobile ? MOBILE_ITEM_COUNT : DESKTOP_ITEM_COUNT;
    queryParams.availability = queryParams.availabilities;
    queryParams.priceRanges = queryParams.prices;
    queryParams.t1 = queryParams.tier1;
    queryParams.t2 = queryParams.tier2;
    queryParams.t3 = queryParams.tier3;
    queryParams.year = urlInformation.year || queryParams.year || fragmentYear;
    queryParams.make = urlInformation.make || queryParams.make || fragmentMake;
    queryParams.model = urlInformation.model || queryParams.model || fragmentModel;

    if (businessCustomer) {
      queryParams.deliveryMethods = [DELIVERY_METHODS.DELIVERY];
    }

    const cleanQP = omit(queryParams, ["tier0", "tier1", "tier2", "tier3", "prices", "availabilities"]);
    if (validateVehicle(selectedVehicle)) {
      cleanQP.make = selectedVehicle.make;
      cleanQP.model = selectedVehicle.model;
      cleanQP.year = selectedVehicle.year;
    }

    const fitmentOptions = selectedVehicle?.fitmentOptions;
    fitmentOptions?.forEach((fo) => {
      if (cleanQP["fo.name"]) {
        cleanQP["fo.name"].push(fo.optionName);
      } else {
        cleanQP["fo.name"] = [fo.optionName];
      }
      if (cleanQP["fo.value"]) {
        cleanQP["fo.value"].push(fo.optionValue);
      } else {
        cleanQP["fo.value"] = [fo.optionValue];
      }
    });
    if (Array.isArray(cleanQP.year)) {
      PASentry.logException(new Error("Double vehicle client", null, { pathname, selectedVehicle, cleanQP }));
    }
    const requestUrl = `${getPageDetails}?${qs.stringify(cleanQP, {
      sort: (a: any, b: any) => API_PARAM_ORDERS.indexOf(b) - API_PARAM_ORDERS.indexOf(a),
    })}`;

    function updateKeys(data: any) {
      const keysToUpdate = pick(data, ["productsAggregation", "tiersWithUrls", "fitmentOptionsDetails",
        "fitmentOptionsPrompt", "fitmentStatus", "relevantSelectedFitmentOptions",
        "remainingFitmentOptions", "vehicleSelectionOptions"]);
      resolve({ keysToUpdate, skipVehicleSelectorModal });
    }

    // remove selected vehicle from comparison as urls like 2010-honda-civic has different server and client urls but same result;
    if (CATEGORY_PAGE_TYPES.includes(currentPageType) && filterUrl(serverRequestUrl, ["year", "make", "model"]) === filterUrl(requestUrl, ["year", "make", "model"]) && !isMounted) {
      updateKeys(actualResponse);
      return;
    }

    if (apiCallsInTransit.has(requestUrl)) {
      return;
    }

    if (!cleanQP.url) {
      return;
    }

    apiCallsInTransit.add(requestUrl);

    axios.get(requestUrl)
      .then(({ data }: any) => {
        isMounted = true;
        updateKeys(data.actualResponse);
      })
      .finally(() => {
        apiCallsInTransit.delete(requestUrl);
      });
  });
}
